import i18n from "@/plugins/i18n";
import cloneDeep from "lodash/cloneDeep";
import { FILTERS } from "@/types/filters";

const UNIT_LIKE_RESOURCE_KEYS = ["LocationLevelUnit", "LocationLevelWard"];
const DEFAULT_ITEM_CHILDREN_KEY = "childLocations";

export function isUnitLikeLocation(location) {
  return UNIT_LIKE_RESOURCE_KEYS.includes(location?.levelNameResourceKey);
}

export function isLocationObservable(location) {
  return location?.isObservable === true;
}

export function isObservableUnitsFilterActive(rootState) {
  return rootState?.[`filters/${FILTERS.OBSERVABLE_UNITS}`]?.activeIds === true;
}

// Returns the Set of location ids observable under the cascade:
// Unit / Ward use their own flag, descendants inherit, ancestors are
// observable when any descendant Unit / Ward is.
export function collectObservableLocationIds(items, itemChildrenKey) {
  const result = new Set();
  if (!Array.isArray(items)) {
    return result;
  }
  const childKey = itemChildrenKey || DEFAULT_ITEM_CHILDREN_KEY;

  function visit(node, ancestorUnitObservable) {
    const children = Array.isArray(node[childKey]) ? node[childKey] : [];

    if (isUnitLikeLocation(node)) {
      const observable = isLocationObservable(node);
      if (observable) {
        result.add(node.id);
      }
      children.forEach((child) => visit(child, observable));
      return observable;
    }

    if (ancestorUnitObservable !== undefined) {
      if (ancestorUnitObservable) {
        result.add(node.id);
      }
      children.forEach((child) => visit(child, ancestorUnitObservable));
      return ancestorUnitObservable;
    }

    let anyDescendantObservable = false;
    children.forEach((child) => {
      if (visit(child, undefined)) {
        anyDescendantObservable = true;
      }
    });
    if (anyDescendantObservable) {
      result.add(node.id);
    }
    return anyDescendantObservable;
  }

  items.forEach((root) => visit(root, undefined));
  return result;
}

// Returns the observable id Set for a locations-filter state. Falls back to
// the per-row isObservable flag when the hierarchy tree is unavailable.
export function getObservableLocationIdsFromState(locationsState) {
  if (!locationsState) {
    return new Set();
  }
  const items = locationsState.hierarchy?.items;
  if (Array.isArray(items) && items.length > 0) {
    return collectObservableLocationIds(
      items,
      locationsState.itemChildrenKey || DEFAULT_ITEM_CHILDREN_KEY
    );
  }
  const fallback = new Set();
  const levels = locationsState.hierarchy?.levels;
  if (levels) {
    Object.values(levels).forEach((levelArr) => {
      if (!Array.isArray(levelArr)) {
        return;
      }
      levelArr.forEach((loc) => {
        if (isLocationObservable(loc)) {
          fallback.add(loc.id);
        }
      });
    });
  }
  return fallback;
}

// Returns the subset of `candidateIds` present in the observable Set.
export function filterToObservableIds(candidateIds, observableIds) {
  if (!Array.isArray(candidateIds) || !(observableIds instanceof Set)) {
    return candidateIds;
  }
  return candidateIds.filter((id) => observableIds.has(id));
}

// Deep-clones the hierarchy tree and tags non-observable nodes with
// `disabled: true` so v-treeview grays them out and blocks toggling.
export function markNonObservableAsDisabled(items, itemChildrenKey) {
  if (!Array.isArray(items)) {
    return items;
  }
  const childKey = itemChildrenKey || DEFAULT_ITEM_CHILDREN_KEY;
  const observableIds = collectObservableLocationIds(items, childKey);

  const decorate = (node) => {
    const cloned = { ...node, disabled: !observableIds.has(node.id) };
    const children = node[childKey];
    if (Array.isArray(children)) {
      cloned[childKey] = children.map(decorate);
    }
    return cloned;
  };
  return items.map(decorate);
}

export default {
  getLocationsHierarchy({ byId, locationLevel, facilityHierarchy }) {
    const clonedHierarchy = cloneDeep(facilityHierarchy);

    if (locationLevel) {
      removeChildrenFromLocationAtLevel(clonedHierarchy, locationLevel);
    }

    const levels = {};
    for (const item of Object.values(byId)) {
      const location = cloneDeep(item);
      if (levels[location.level]) {
        levels[location.level].push(location);
      } else {
        levels[location.level] = [location];
      }
    }

    return {
      items: clonedHierarchy.childLocations,
      levels
    };
  },
  getLocationsSelectedText(state) {
    const { activeIds, byId, hierarchy } = state;

    if (!hierarchy || !hierarchy.levels) {
      return;
    }
    if (!activeIds || (activeIds instanceof Array && activeIds.length === 0)) {
      return i18n.t("ui.none-selected");
    }

    const levelInfo = {};
    for (const [levelNum, locations] of Object.entries(hierarchy.levels)) {
      if (levelNum > 1 && locations && locations.length) {
        levelInfo[levelNum] = {
          count: 0,
          totalCount: locations.length,
          resourceKey: locations[0].levelNameResourceKey
        };
      }
    }
    activeIds.forEach((id) => {
      levelInfo[byId[id].level].count++;
    });

    const levelText = [];
    Object.keys(levelInfo)
      .sort()
      .forEach((level) => {
        const levelCount = levelInfo[level].count;
        const pluralizedName =
          levelCount === 1
            ? i18n.t(
                `locations.${levelInfo[
                  level
                ].resourceKey.toLocaleLowerCase()}-singular`
              )
            : i18n.t(
                `locations.${levelInfo[
                  level
                ].resourceKey.toLocaleLowerCase()}-plural`
              );
        levelText.push(`${levelCount} ${pluralizedName}`);
      });
    return levelText.join(", ");
  },
  getLocationsFilterText(state, getters) {
    const { activeIds, byId, hierarchy } = state;
    const { selectableIds } = getters;
    if (!hierarchy || !hierarchy.levels) {
      return null;
    }
    if (!activeIds || (activeIds instanceof Array && activeIds.length === 0)) {
      return i18n.t("ui.none-selected");
    }
    if (selectableIds.length === activeIds.length) {
      return i18n.t("ui.all-selected");
    }

    const levelInfo = {};
    for (const [levelNum, locations] of Object.entries(hierarchy.levels)) {
      if (levelNum > 1 && locations && locations.length) {
        levelInfo[levelNum] = {
          count: 0,
          totalCount: locations.length,
          name: locations[0].levelName,
          resourceKey: locations[0].levelNameResourceKey
        };
      }
    }

    activeIds.forEach((id) => {
      levelInfo[byId[id].level].count++;
    });

    const levelText = [];
    Object.keys(levelInfo)
      .sort()
      .forEach((level) => {
        const totalLevelCount = levelInfo[level].totalCount;
        const countOf = i18n.t("count.count-of-total", {
          count: levelInfo[level].count,
          total: totalLevelCount
        });
        const pluralizedName =
          totalLevelCount === 1
            ? i18n.t(
                `locations.${levelInfo[
                  level
                ].resourceKey.toLocaleLowerCase()}-singular`
              )
            : i18n.t(
                `locations.${levelInfo[
                  level
                ].resourceKey.toLocaleLowerCase()}-plural`
              );
        levelText.push(`${countOf} ${pluralizedName}`);
      });
    const displayText = levelText.join(", ");
    return displayText;
  },
  getLocationsFilterTextAbbreviated(state, getters) {
    const { activeIds } = state;
    const { selectableIds } = getters;
    if (!selectableIds || !selectableIds.length) {
      return null;
    }
    if (!activeIds || (activeIds instanceof Array && activeIds.length === 0)) {
      return i18n.t("ui.none-selected");
    }
    if (selectableIds.length === activeIds.length) {
      return i18n.t("ui.all-selected");
    }
    const totalCount = selectableIds.length;
    const selectedCount = activeIds.length;
    const locationList = firstNlocations(state, 10)
      .map((location) => location.text)
      .join(", ");
    return `${i18n.t("count.count-of-total", {
      count: selectedCount,
      total: totalCount
    })}: ${locationList}`;
  },
  getLocationsSelectedTextDetails(state, getters, rootState) {
    const { activeIds, hierarchy } = state;
    if (!hierarchy || !hierarchy.levels) {
      return [];
    }
    if (!activeIds || (activeIds instanceof Array && activeIds.length === 0)) {
      return [];
    }

    // Mirrors the picker gating: drop non-observable rows under the cascade.
    const includeOnlyObservableUnits = isObservableUnitsFilterActive(rootState);
    const observableIds = includeOnlyObservableUnits
      ? getObservableLocationIdsFromState(state)
      : null;

    const levelInfo = {};
    for (const [levelNum, locations] of Object.entries(hierarchy.levels)) {
      if (levelNum > 1 && locations && locations.length) {
        levelInfo[levelNum] = {
          totalCount: locations.length,
          name: locations[0].levelName,
          resourceKey: locations[0].levelNameResourceKey,
          selectedLocations: []
        };
        locations.forEach((location) => {
          if (!activeIds.includes(location.id)) {
            return;
          }
          if (includeOnlyObservableUnits && !observableIds.has(location.id)) {
            return;
          }
          levelInfo[levelNum].selectedLocations.push(location);
        });
      }
    }

    const detailsText = [];
    Object.keys(levelInfo)
      .sort()
      .forEach((level) => {
        const resourceKey = levelInfo[level].resourceKey.toLocaleLowerCase();
        const label =
          includeOnlyObservableUnits && resourceKey === "locationlevelunit"
            ? i18n.t("filters.observableUnits")
            : i18n.t(`locations.${resourceKey}-plural`);
        let text;
        if (levelInfo[level].selectedLocations.length > 0) {
          text = levelInfo[level].selectedLocations
            .map((l) => l.text)
            .sort((a, b) => a.localeCompare(b))
            .join(", ");
        } else {
          text = i18n.t("ui.none-selected");
        }
        detailsText.push({ label, text });
      });
    return detailsText;
  }
};

export function createFlatList({ levelInfo, locationHierarchy }) {
  const locationsList = [];

  function addChildrenToList(location) {
    location.childLocations.forEach((l) => {
      l.text = l.name;
      const levelNameResourceKey = levelInfo[l.level.toString()];
      // Also tag the tree node so consumers can identify Unit / Ward
      // levels without a byId lookup.
      l.levelNameResourceKey = levelNameResourceKey;

      locationsList.push({
        id: l.id,
        name: l.name,
        parentId: l.parentId,
        level: l.level,
        levelNameResourceKey: levelNameResourceKey,
        levelName: i18n.t(`locations.levelNames.${levelNameResourceKey}`),
        isObservable: l.isObservable
      });

      addChildrenToList(l);
    });
  }

  addChildrenToList(locationHierarchy);

  return locationsList;
}

function firstNlocations(state, count) {
  const { activeIds, hierarchy } = state;
  let counter = 0;
  const firstN = [];
  const traverse = (locations) => {
    if (counter >= count) {
      return;
    }
    locations.forEach((location) => {
      if (counter >= count) {
        return;
      }
      if (activeIds.includes(location.id)) {
        firstN.push(location);
        counter++;
      }
      if (location.childLocations) {
        traverse(location.childLocations);
      }
    });
  };
  traverse(hierarchy.items);
  return firstN;
}

function removeChildrenFromLocationAtLevel(location, level) {
  if (location.level === level) {
    delete location.childLocations;
  } else {
    location.childLocations.forEach((l) => {
      removeChildrenFromLocationAtLevel(l, level);
    });
  }
}
